import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorMessage } from '../../../utils';

export const STATUS_UPDATE_FAILED_MESSAGE =
  'Could not update that status. The list has been refreshed.';

/**
 * The select only offers legal transitions, so a rejection means the row
 * moved underneath us — by the time it lands the optimistic patch is rolled
 * back and invalidated, so the list on screen is authoritative again. The
 * awards-service 409 bodies are written for admins: relay them verbatim, and
 * fall back to the shared copy when the rejection carries no body. Callers
 * with different needs override via options.onError.
 */
const defaultOnError = (error) => {
  showErrorMessage({
    message: error?.response?.data?.error ?? STATUS_UPDATE_FAILED_MESSAGE,
  });
};

/**
 * Shared machinery for the two membership status-edit hooks (the cycle roster
 * and the profile Promotion tab): optimistically rewrite every cached page
 * under `snapshotKey` via `patchPage`, roll the snapshots back verbatim if the
 * server rejects, and invalidate `invalidateKeys(variables)` on settle.
 *
 * The rollback restores whole-page snapshots, so with two edits genuinely in
 * flight at once the first one's failure can transiently revert the second's
 * optimistic patch — the settle-time invalidation refetch converges the cache
 * either way. Both callers therefore keep per-row edits serialized in the UI
 * (an in-flight row's control is disabled).
 */
export const useOptimisticStatusMutation = (
  { mutationFn, snapshotKey, patchPage, invalidateKeys },
  options = {},
) => {
  const { onError = defaultOnError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    meta: { skipGlobalErrorToaster: true },
    mutationFn,
    onMutate: async (variables) => {
      const filter = { queryKey: snapshotKey };
      await queryClient.cancelQueries(filter);

      const snapshots = queryClient.getQueriesData(filter);

      snapshots.forEach(([key, data]) => {
        if (!data?.items) return;

        queryClient.setQueryData(key, patchPage(data, variables));
      });

      return { snapshots };
    },
    onError: (error, _variables, context) => {
      context?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      onError(error);
    },
    onSettled: (_data, _error, variables) => {
      invalidateKeys(variables).forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
  });
};
